import React, { useRef } from 'react';
import { Textarea } from './textarea';
import { Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, AlignJustify, Heading1, Heading2, Link2, Image as ImageIcon, MousePointerClick } from 'lucide-react';
import { Button } from './button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './dropdown-menu';

interface RichTextareaProps extends React.ComponentProps<typeof Textarea> {
  value: string;
  onChange: (e: any) => void;
}

export function RichTextarea({ value, onChange, className, ...props }: RichTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const applyFormat = (tag: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    if (start === end) return;

    const selectedText = value.substring(start, end);
    const beforeText = value.substring(0, start);
    const afterText = value.substring(end);

    const openTag = `<${tag}>`;
    const closeTag = `</${tag}>`;

    const newText = `${beforeText}${openTag}${selectedText}${closeTag}${afterText}`;
    
    const event = {
      target: {
        value: newText
      }
    } as any;
    
    onChange(event);
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + openTag.length, end + openTag.length);
    }, 0);
  };

  const applyColor = (colorClass: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    if (start === end) return;

    const selectedText = value.substring(start, end);
    const beforeText = value.substring(0, start);
    const afterText = value.substring(end);

    const openTag = `<span class="${colorClass}">`;
    const closeTag = `</span>`;

    const newText = `${beforeText}${openTag}${selectedText}${closeTag}${afterText}`;
    
    const event = {
      target: {
        value: newText
      }
    } as any;
    
    onChange(event);
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + openTag.length, end + openTag.length);
    }, 0);
  };

  const applyClassWrap = (className: string, tag: string = 'div') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    const selectedText = value.substring(start, end) || 'Texte...';
    const beforeText = value.substring(0, start);
    const afterText = value.substring(end);

    const openTag = `<${tag} class="${className}">`;
    const closeTag = `</${tag}>`;

    const newText = `${beforeText}${openTag}${selectedText}${closeTag}${afterText}`;
    
    onChange({ target: { value: newText } } as any);
    setTimeout(() => { textarea.focus(); textarea.setSelectionRange(start + openTag.length, start + openTag.length + selectedText.length); }, 0);
  };

  const applyLink = () => {
    const url = window.prompt("Entrez l'URL du lien :");
    if (!url) return;
    applyClassWrap(`text-primary underline" href="${url}`, 'a');
  };

  const applyAdvancedButton = (styleClass: string) => {
    const url = window.prompt("Entrez l'URL de destination du bouton :");
    if (!url) return;
    applyClassWrap(`${styleClass} inline-block px-6 py-3 font-bold rounded-xl hover:opacity-90 transition-opacity mt-2 mb-2" href="${url}`, 'a');
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64Url = reader.result as string;
      const textarea = textareaRef.current;
      if (!textarea) return;
      
      const start = textarea.selectionStart;
      const beforeText = value.substring(0, start);
      const afterText = value.substring(start);
      
      const imgTag = `<img src="${base64Url}" alt="Image uploadée" class="w-full h-auto rounded-xl my-4 shadow-sm border border-border/50" />\n`;
      
      onChange({ target: { value: beforeText + imgTag + afterText } } as any);
      setTimeout(() => { textarea.focus(); textarea.setSelectionRange(start + imgTag.length, start + imgTag.length); }, 0);
    };
    reader.readAsDataURL(file);
    
    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className={`space-y-1 ${className || ''}`}>
      <input 
        type="file" 
        accept="image/*" 
        className="hidden" 
        ref={fileInputRef} 
        onChange={handleImageUpload} 
      />
      <div className="flex flex-wrap items-center gap-1 bg-muted/30 p-1 rounded-md border border-border">
        <Button 
          type="button" 
          variant="ghost" 
          size="sm" 
          className="h-7 w-7 p-0" 
          onClick={() => applyFormat('b')}
          title="Gras"
        >
          <Bold className="w-3 h-3" />
        </Button>
        <Button 
          type="button" 
          variant="ghost" 
          size="sm" 
          className="h-7 w-7 p-0" 
          onClick={() => applyFormat('i')}
          title="Italique"
        >
          <Italic className="w-3 h-3" />
        </Button>
        <Button 
          type="button" 
          variant="ghost" 
          size="sm" 
          className="h-7 w-7 p-0" 
          onClick={() => applyFormat('u')}
          title="Souligné"
        >
          <Underline className="w-3 h-3" />
        </Button>

        <div className="w-px h-4 bg-border mx-1"></div>
        
        <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => applyClassWrap('text-left')} title="Aligner à gauche"><AlignLeft className="w-3 h-3" /></Button>
        <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => applyClassWrap('text-center')} title="Centrer"><AlignCenter className="w-3 h-3" /></Button>
        <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => applyClassWrap('text-right')} title="Aligner à droite"><AlignRight className="w-3 h-3" /></Button>
        <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => applyClassWrap('text-justify')} title="Justifier"><AlignJustify className="w-3 h-3" /></Button>

        <div className="w-px h-4 bg-border mx-1"></div>

        <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => applyClassWrap('text-4xl md:text-5xl font-display font-bold mb-4', 'h1')} title="Grand Titre (H1)"><Heading1 className="w-3 h-3" /></Button>
        <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => applyClassWrap('text-2xl md:text-3xl font-display font-bold mb-3 mt-4', 'h2')} title="Sous-titre (H2)"><Heading2 className="w-3 h-3" /></Button>

        <div className="w-px h-4 bg-border mx-1"></div>

        <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={applyLink} title="Insérer un lien"><Link2 className="w-3 h-3" /></Button>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" title="Insérer un Bouton d'action"><MousePointerClick className="w-3 h-3" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => applyAdvancedButton('bg-primary text-primary-foreground')}>Bouton Primaire (Couleur principale)</DropdownMenuItem>
            <DropdownMenuItem onClick={() => applyAdvancedButton('bg-secondary text-secondary-foreground')}>Bouton Secondaire (Orange)</DropdownMenuItem>
            <DropdownMenuItem onClick={() => applyAdvancedButton('border-2 border-primary text-primary bg-transparent')}>Bouton Contour (Outline)</DropdownMenuItem>
            <DropdownMenuItem onClick={() => applyAdvancedButton('bg-black text-white')}>Bouton Noir</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => fileInputRef.current?.click()} title="Insérer une image (Upload)"><ImageIcon className="w-3 h-3" /></Button>

        <div className="w-px h-4 bg-border mx-1"></div>
        <button 
          type="button"
          onClick={() => applyColor('text-primary')}
          className="w-4 h-4 rounded-full bg-primary hover:scale-110 transition-transform"
          title="Couleur Principale"
        />
        <button 
          type="button"
          onClick={() => applyColor('text-secondary')}
          className="w-4 h-4 rounded-full bg-secondary hover:scale-110 transition-transform"
          title="Couleur Secondaire"
        />
        <button 
          type="button"
          onClick={() => applyColor('text-destructive')}
          className="w-4 h-4 rounded-full bg-destructive hover:scale-110 transition-transform"
          title="Couleur Alerte"
        />
        <button 
          type="button"
          onClick={() => applyColor('text-emerald-500')}
          className="w-4 h-4 rounded-full bg-emerald-500 hover:scale-110 transition-transform"
          title="Couleur Succès"
        />
      </div>
      <Textarea 
        ref={textareaRef}
        value={value}
        onChange={onChange}
        {...props} 
      />
    </div>
  );
}
